// @ts-nocheck
import React, { useState, useRef, useEffect } from "react";
import * as THREE from "three";
import { Canvas, useThree } from "@react-three/fiber";
import { Line, OrbitControls, Text, TransformControls } from "@react-three/drei";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import SatDesignerSideBar from "./SatDesignerSideBar";
import SensorInfoCard from "./SensorInfoCard";
import SatDesignerBottomBar from "./SatDesignerBottomBar";
import SpikeModelInfoCard from "./SpikeModelInfoCard";
import SatDesignerBottomBarModal from "./SatDesignerBottomBarModals/SatDesignerBottomBarModals";
import SensorAnalysisReportComponent from "./SensorAnalysisReportSection/SensorAnalysisReportComponent";
import { ReportBtnIconGrey } from "../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import { useNavigate, useParams } from "react-router-dom";
import MissionService from "../../../../../service/MissionService";
import { extractAndLoadModelFromZipUrl, offline, scaleScene, setLightingInCanvas, showToastMessage } from "../../../../../utils/commonFunctions";
import DefaultSatModel from "../../../../CommonComponents/DefaultSatModel";
import ConfirmationDialog from "../../../../CommonComponents/confirmation_dialog/ConfirmationDialog";
import NewLoader from "../../../../CommonComponents/NewLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import CommonCanvas from "../../../../CommonComponents/CommonCanvas";

import ThermalDesigner from "./ThermalDesigner/ThermalDesigner";
import FloorDotGrid from "../../../../simulate/my-scenario/components/scenario-timeline/thermal-heatmap/components/visualization/FloorDotGrid";
import { SatCubePreview } from "./ThermalDesigner/components/visualization";
import { useThermalDesign } from "../ThermalDesignContext";

function CreateMesh(props) {
  const meshRef = useRef();
  const linesRef = useRef();
  const groupRef = useRef();
  const transformControlsRef = useRef();

  useEffect(() => {
    if (groupRef.current) {
      // Apply orientation to the group
      groupRef.current.rotation.x = THREE.MathUtils.degToRad(props.orientation.x);
      groupRef.current.rotation.y = THREE.MathUtils.degToRad(props.orientation.y);
      groupRef.current.rotation.z = THREE.MathUtils.degToRad(props.orientation.z);
    }
  }, [props.orientation]);

  useEffect(() => {
    const transformControls = transformControlsRef.current;
    const handleDragStart = () => {
      if (props.orbitControlsRef?.current) {
        props.orbitControlsRef.current.enabled = false; // Disable OrbitControls
      }
    };
    const handleDragEnd = () => {
      if (props.orbitControlsRef?.current) {
        props.orbitControlsRef.current.enabled = true; // Enable OrbitControls
      }
    };
    if (transformControls) {
      transformControls.addEventListener("dragging-changed", (event) => {
        if (event.value) {
          handleDragStart(); // When dragging starts
        } else {
          handleDragEnd(); // When dragging ends
        }
      });
    }
    return () => {
      if (transformControls) {
        transformControls.removeEventListener("dragging-changed", handleDragStart);
        transformControls.removeEventListener("dragging-changed", handleDragEnd);
      }
    };
  }, [props.orbitControlsRef]);

  return (
    <>
      <TransformControls enabled={false} ref={transformControlsRef} object={groupRef.current} name={"transform_controls"}>
        <group ref={groupRef} name="sensor_component_group">
          <mesh visible={props.visible} ref={meshRef} position={[props.position[0], props.position[1], props.position[2]]} name={"bus_components"}>
            <boxGeometry args={props.args} attach="geometry" />
            <meshPhysicalMaterial
              attach="material"
              color="#DAA520"
              emissive="#FF8C00" // Soft glow
              emissiveIntensity={0.5}
              metalness={0.9}
              roughness={0.2}
            />
          </mesh>
          <lineSegments ref={linesRef} name={"line_segments"}>
            <edgesGeometry attach="geometry" args={[new THREE.BoxGeometry(...props.args)]} />
            <lineBasicMaterial attach="material" color="#FFFFFF" /> {/* White border */}
          </lineSegments>
        </group>
      </TransformControls>
    </>
  );
}

const clearSceneByName = (scene, name) => {
  // const { scene } = useThree();
  scene.children
    .filter((child) => child.name === name)
    .forEach((child) => {
      scene.remove(child); // Remove the child from the scene
      child.geometry?.dispose(); // Dispose of geometry resources
      child.material?.dispose(); // Dispose of material resources
    });
};

const CommonSat = () => {
  const { camera, gl, scene } = useThree();
  return (
    <group>
      <ambientLight intensity={1} />
      <spotLight intensity={5} position={[-10, 10, 10]} angle={0.3} />
    </group>
  );
};

function SceneManager({ setSelectedMesh }) {
  const raycaster = useRef(new THREE.Raycaster());
  const { camera, scene, gl } = useThree();

  useEffect(() => {
    const handleMouseDown = (event) => {
      const mouse = new THREE.Vector2();
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      raycaster.current.setFromCamera(mouse, camera);
      const intersects = raycaster.current.intersectObjects(scene.children);

      for (let i = 0; i < intersects.length; i++) {
        const intersectedMesh = intersects[i].object;
        if (intersectedMesh.name === "bus_components") {
          setSelectedMesh("bus_components");
          break;
        }
      }
    };

    gl.domElement.addEventListener("mousedown", handleMouseDown);

    return () => {
      gl.domElement.removeEventListener("mousedown", handleMouseDown);
    };
  }, [camera, scene, gl]);
}

const Custom3DSat = ({ busType, busSizeUsage }) => {
  const params = useParams();
  const { mission_id, sat_id } = params;
  const navigate = useNavigate();
  const orbitControlsRef = useRef(null);
  const transformControlsRef = useRef();

  const [isReportBarOpen, setIsReportBarOpen] = useState(false);
  const [isAddControlModeModal, setIsAddControlModeModal] = useState(false);
  const [prevControlmode, setPrevControlmode] = useState(null);
  const [meshes, setMeshes] = useState([]);
  const [selectedMesh, setSelectedMesh] = useState(null);
  const [orientation, setOrientation] = useState({ x: 0, y: 0, z: 0 });
  const [allSensors, setAllSensors] = useState<any>();
  const [controlModes, setControlmodes] = useState<any>();
  const [selectedSensor, setSelectedSensor] = useState<any>();
  const [selectedSensorHeader, setSelectedSensorHeader] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [payloads, setPayloads] = useState();
  const [model, setModel] = useState(null);
  const [newLoading, setNewLoading] = useState(true);
  const { openModal } = useThermalDesign();
  const isThermalModalOpen = openModal.isNodeSpecsModalOpen || openModal.isNodeSpecsJsonModalOpen || openModal.isThermalConductivityModalOpen || openModal.isThermalConductivityJsonModalOpen || openModal.isSurfaceThresholdsModalOpen;
  const [newSensorAnalysis, setNewSensorAnalysis] = useState(false);
  const [duration, setDuration] = useState();
  const [controlMode, setControlMode] = useState(true);
  const [openDeleteDialouge, setOpenDeleteDialouge] = useState(false);
  const [currentControlMode, setCurrentControlMode] = useState();
  const [payloadSizeUsage, setPayloadSizeUsage] = useState();
  const [totalSize, setTotalSize] = useState();
  const [meshVisibilityMap, setMeshVisibilityMap] = useState<{ [key: string]: boolean }>({});

  const reportProps = { isReportBarOpen, setIsReportBarOpen };
  const controlModeModalProps = { isAddControlModeModal, setIsAddControlModeModal };
  const [isModalMinimized, setIsModalMinimized] = useState(false);
  const [headerObject, setHeaderObject] = useState({
    title: prevControlmode ? "Edit Control Mode" : "Add Control Mode",
    isModalMinimized: isModalMinimized,
    setIsModalMinimized: setIsModalMinimized,
  });
  const [isValueUpdate, setIsValueUpdate] = useState(false);

  const loader = new GLTFLoader();

  const durationObject = [
    {
      label: "1 Month",
      value: "1",
    },
    {
      label: "2 Month",
      value: "2",
    },
    {
      label: "6 Month",
      value: "6",
    },
    {
      label: "8 Month",
      value: "8",
    },
    {
      label: "12 Month",
      value: "12",
    },
  ];

  const addMesh = (newMesh) => {
    const meshWithName = {
      ...newMesh,
      name: newMesh?.name ?? "sensor-mesh-group",
    };
    setMeshes(() => [meshWithName]);
    setControlMode(false);
    setSelectedMesh("bus_components");
    // Set visibility to true for new mesh
    setMeshVisibilityMap((prev) => ({
      ...prev,
      [meshWithName.name]: true,
      ["sensor-mesh-group"]: true,
    }));
  };

  const handleOrientationChange = (newOrientation) => {
    setOrientation(newOrientation); // Update the orientation state in Custom3DSat
  };

  const get_control_modes = () => {
    MissionService.getControlModes(sat_id)
      .then((res) => {
        if (res) {
          setControlmodes(res?.data);
        }
      })
      .catch((error) => {
        const message = "An error occurred while fetching control modes. Please try again.";
        showToastMessage("error", message);
      });
  };

  const get_all_sensors = () => {
    MissionService.getAllSensors(sat_id)
      .then((res) => {
        if (res) {
          setAllSensors(res?.data);
        }
      })
      .catch((error) => {
        const message = "An error occurred while fetching sensors. Please try again.";
        showToastMessage("error", message);
      });
  };

  const calculatePayloadUsage = (allPayloads) => {
    let size = 0;
    allPayloads?.forEach((v) => {
      if (v?.["user-data"]) {
        size = size + Number(v["atmos-data"]?.["product-cookie"]?.["Size"]);
      } else if (v?.["user_data"]) {
        size = size + Number(v["atmos-data"]?.["product-cookie"]?.["Size"]);
      } else {
        size = size + Number(v["atmosData"]?.["product-cookie"]?.["Size"]);
      }
      setPayloadSizeUsage(size);
    });
    return size;
  };

  const get_all_paylods = () => {
    MissionService.get_payload_by_sat_id(sat_id, mission_id)
      ?.then((res) => {
        if (res) {
          setPayloads(res?.data);
          const all_payloads = res?.data ? res?.data : [];
          calculatePayloadUsage(all_payloads);
        }
      })
      .catch((error) => {
        const message = "An error occurred while fetching payloads. Please try again.";
        showToastMessage("error", message);
      });
  };

  useEffect(() => {
    if (busSizeUsage && payloadSizeUsage) {
      setTotalSize(busSizeUsage + payloadSizeUsage);
    }
  }, [busSizeUsage, payloadSizeUsage]);

  useEffect(() => {
    if (!loader) return;
    if (!offline()) {
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.6/");
      loader.setDRACOLoader(dracoLoader);
    }
  }, [loader]);

  const addControlMode = (body) => {
    MissionService.postControlMode(body, sat_id)
      .then((res) => {
        if (res) {
          showToastMessage("success", "Control Mode added successfully");
          get_control_modes();
          setIsAddControlModeModal(false);
        }
      })
      .catch((error) => {
        const message = typeof error?.response?.data?.message === "string" ? error.response?.data?.message : "An error occurred while adding Control Mode. Please try again.";
        showToastMessage("error", message);
      });
  };

  const updateControlMode = (body, name) => {
    MissionService.updateControlMode(body, sat_id, name)
      .then((res) => {
        if (res) {
          showToastMessage("success", "Control Mode updated successfully");
          get_control_modes();
          setIsAddControlModeModal(false);
        }
      })
      .catch((error) => {
        const message = typeof error?.response?.data?.message === "string" ? error?.response?.data?.message : "An error occurred while updating Control Mode. Please try again.";
        showToastMessage("error", message);
      });
  };

  const startSensorAnalysis = (data) => {
    setLoading(true);
    MissionService.sensorAnalysis(data, sat_id)
      ?.then((response) => {
        if (response) {
          setLoading(false);
          setNewSensorAnalysis(true);
          setIsReportBarOpen(true);
          const message = `Analysis completed successfully`;
          showToastMessage("success", message);
        }
      })
      .catch((error) => {
        setNewSensorAnalysis(false);
        setLoading(false);
        const message = `An error occurred while Analysis. Please try again.`;
        showToastMessage("error", message);
      });
  };

  const loadModel = (modelUrl, modelName) => {
    try {
      loader.load(
        modelUrl,
        (gltf) => {
          const model = gltf.scene;
          // Calculate the size of the model and set the scale
          const box = new THREE.Box3().setFromObject(model);
          const size = new THREE.Vector3();
          box.getSize(size);

          // Set the target size (2 unit)
          const targetSize = 10;
          const maxDimension = Math.max(size.x, size.y, size.z);
          const scaleFactor = targetSize / maxDimension;

          // Apply the calculated scale factor uniformly
          model.scale.set(scaleFactor, scaleFactor, scaleFactor);

          setModel(model); // Model loaded with adjusted scale
          setNewLoading(false); // Update loading state
        },
        undefined, // Progress callback (optional)
        (error) => {
          setNewLoading(false); // Update loading state on error
          const message = typeof error?.response?.data?.message === "string" ? error?.response?.data?.message : `An error occurred while loading model. Please try again.`;
          showToastMessage("error", message);
        }
      );
    } catch (error) {
      setNewLoading(false);
      const message = typeof error?.response?.data?.message === "string" ? error?.response?.data?.message : `An error occurred while loading model. Please try again.`;
      showToastMessage("error", message);
    }
  };

  const getModelUrlByName = async (satelliteId, modelName) => {
    try {
      const response = await MissionService.getModelUrl(satelliteId, modelName);
      if (response?.data) {
        const fileName = response?.data.split('?')[0].split('/').pop();
        const isZip = fileName.toLowerCase().endsWith('.zip');
        if (isZip) {
          handleZipModelFromUrl(response?.data);
        }
        else {
          loadModel(response?.data, modelName);
        }
      } else {
        setModel(null);
        setNewLoading(false);
      }
    } catch (error) {
      const message = typeof error?.response?.data?.message === "string" ? error?.response?.data?.message : `An error occurred while fetching model URL. Please try again.`;
      showToastMessage("error", message);
      setModel(null);
      setNewLoading(false);
    }
  };

  const getSatelliteById = async (satelliteId) => {
    try {
      const response = await MissionService.get_satellite_by_sat_id(satelliteId, mission_id);
      const satModelName = response?.data?.["modelNames"]?.["modelName"];
      if (satModelName) {
        await getModelUrlByName(satelliteId, satModelName);
      } else {
        setModel(null);
        setNewLoading(false);
      }
    } catch (error) {
      const message = typeof error?.response?.data?.message === "string" ? error?.response?.data?.message : `An error occurred while fetching satellite. Please try again.`;
      showToastMessage("error", message);
      setModel(null);
      setNewLoading(false);
    }
  };

  const fetchModelData = async () => {
    await getSatelliteById(sat_id);
  };

  useEffect(() => {
    get_control_modes();
    get_all_sensors();
    get_all_paylods();
    fetchModelData();
  }, [sat_id]);

  useEffect(() => {
    if (!model) return;
    let assigned = false;
    const interval = setInterval(() => {
      if (transformControlsRef.current && !assigned) {
        transformControlsRef.current.object = model;
        transformControlsRef.current.rotation.copy(model.rotation);
        transformControlsRef.current.showX = true;
        transformControlsRef.current.showY = true;
        transformControlsRef.current.showZ = true;
        transformControlsRef.current.enabled = false;
        assigned = true;
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [model]);

  useEffect(() => {
    if (selectedSensor) {
      const sensorOrientation = selectedSensor[0]?.node?.value?.orientation;
      if (Array.isArray(sensorOrientation)) {
        setOrientation({
          x: sensorOrientation[0],
          y: sensorOrientation[1],
          z: sensorOrientation[2],
        });
      }
    }
  }, [selectedSensor]);

  const deleteSelectedControlMode = () => {
    if (!currentControlMode) return;
    MissionService.deleteControlMode(sat_id, currentControlMode)
      ?.then((response) => {
        if (response) {
          const message = `Control Mode deleted successfully`;
          showToastMessage("success", message);
          get_control_modes();
        }
      })
      .catch((error) => {
        const message = `An error occurred while deleting control mode. Please try again.`;
        showToastMessage("error", message);
      });
  };

  const SceneVisibilityManager = ({ meshVisibilityMap }) => {
    const { scene } = useThree();
    useEffect(() => {
      scene.traverse((child) => {
        // Match individual meshes
        if (child.name && meshVisibilityMap.hasOwnProperty(child.name)) {
          child.visible = meshVisibilityMap[child.name];
        }
      });
    }, [meshVisibilityMap, scene]);
    return null;
  };
  const handleZipModelFromUrl = async (url: string) => {
      setNewLoading(true);
      const result = await extractAndLoadModelFromZipUrl(url, { targetSize: 10 });
      if (result?.scene) {
          setModel(result.scene);
      }
      setNewLoading(false);
  };

  return (

      <div style={{ height: window.innerHeight, width: window.innerWidth, overflow: "hidden" }}>
        {!controlMode && (
          <Canvas camera={{ position: [0, 0, 10] }} style={{ background: "#000", width: "100vw", height: "100vh" }}>
            <OrbitControls enableRotate={false} ref={orbitControlsRef} enableZoom={false} minDistance={5} maxDistance={100} target={[0, 0, 0]} name="Orbit Controls" />
            <SceneVisibilityManager meshVisibilityMap={meshVisibilityMap} />
            <CommonSat />
            {/* {setLightingInCanvas()} */}
            <group name="sensor-mesh-group">
              {meshes.map((meshProps) => (
                <CreateMesh
                  {...meshProps}
                  visible={meshVisibilityMap[meshProps.name] ?? true} // default to visible
                  orbitControlsRef={orbitControlsRef} // Pass orbitControlsRef here
                  orientation={orientation}
                />
              ))}
            </group>
            <SceneManager orbitControlsRef={orbitControlsRef} setSelectedMesh={setSelectedMesh} />
          </Canvas>
        )}

        <SatDesignerSideBar
          addMesh={addMesh}
          allSensors={allSensors}
          setSelectedSensor={setSelectedSensor}
          setSelectedSensorHeader={setSelectedSensorHeader}
          payloads={payloads}
          selectedSensor={selectedSensor}
          meshVisibilityMap={meshVisibilityMap}
          onToggleVisibility={(name) => {
            setMeshVisibilityMap((prev) => ({
              ...prev,
              [name]: !(prev[name] ?? true),
            }));
          }}
        />

        {selectedMesh === "bus_components" && selectedSensor && (
          <SensorInfoCard
            reportProps={reportProps}
            orientation={orientation}
            onOrientationChange={handleOrientationChange}
            selectedSensors={selectedSensor}
            controlModes={controlModes}
            startSensorAnalysis={startSensorAnalysis}
            loading={loading}
            selectedSensorHeader={selectedSensorHeader}
            setDuration={setDuration}
            duration={duration}
            durationObject={durationObject}
            setNewSensorAnalysis={setNewSensorAnalysis}
            newSensorAnalysis={newSensorAnalysis}
          />
        )}

        {!isReportBarOpen && (
          <button
            onClick={() => {
              setIsReportBarOpen(!isReportBarOpen);
            }}
            className="report_icon_rectangle"
          >
            <ReportBtnIconGrey />
          </button>
        )}

        <SatDesignerBottomBar setControlMode={setControlMode} setSelectedMesh={setSelectedMesh} controlMode={controlMode} />

        {selectedMesh != "bus_components" && (
          <SpikeModelInfoCard
            reportProps={reportProps}
            controlModeModalProps={controlModeModalProps}
            controlModes={controlModes}
            setPrevControlmode={setPrevControlmode}
            setOpenDeleteDialouge={setOpenDeleteDialouge}
            setCurrentControlMode={setCurrentControlMode}
            setIsModalMinimized={setIsModalMinimized}
            isModalMinimized={isModalMinimized}
            setHeaderObject={setHeaderObject}
            headerObject={headerObject}
            setIsValueUpdate={setIsValueUpdate}
          />
        )}

        {isReportBarOpen && (
          <SensorAnalysisReportComponent
            reportProps={reportProps}
            isReportBarOpen={isReportBarOpen}
            newSensorAnalysis={newSensorAnalysis}
            setNewSensorAnalysis={setNewSensorAnalysis}
          />
        )}

        {isAddControlModeModal && selectedMesh != "bus_components" && (
          <SatDesignerBottomBarModal
            controlModeModalProps={controlModeModalProps}
            addControlMode={addControlMode}
            updateControlMode={updateControlMode}
            prevControlmode={prevControlmode}
            setIsModalMinimized={setIsModalMinimized}
            isModalMinimized={isModalMinimized}
            setHeaderObject={setHeaderObject}
            headerObject={headerObject}
            isValueUpdate={isValueUpdate}
            setIsValueUpdate={setIsValueUpdate}
          />
        )}

      {(!newLoading && model && !isThermalModalOpen) &&
        <div style={{ position: "absolute", inset: 0 }}>
          <CommonCanvas
            model={model}
            camera={{ position: [-4, 4.5, 12], fov: 105 }}
            controls={{ minDistance: 5, maxDistance: 100, target: [0, 0, 0] }}
            axisSize={5}
            axisLineWidth={1}
            textSize={0.5}
            setLightingInCanvas={setLightingInCanvas}
          >
            <FloorDotGrid floorY={-3.5} extent={20} step={1.2} dotSize={0.5} />
          </CommonCanvas>
        </div>
      }
      {isThermalModalOpen && <SatCubePreview />}
      {(!isThermalModalOpen && !newLoading && !model) && <DefaultSatModel busSize={busType} size={totalSize} canvas_height="100%" canvas_width="100%" isPayloadChanges="false" cubeSize={1}></DefaultSatModel>}
      {/* {(!newLoading && !model) && <DefaultSatModel busSize={busType} size={totalSize} canvas_height="100%" canvas_width="100%" isPayloadChanges="false" cubeSize={1}></DefaultSatModel>} */}


        {newLoading && <NewLoader canvasHeight={"95vh"} canvasWidth={"100%"} />}
        {openDeleteDialouge && currentControlMode && (
          <ConfirmationDialog
            type="delete"
            open={openDeleteDialouge ? true : false}
            title={`Delete Control Mode?`}
            description={`Deleting this Control Mode will permanently remove it. Reports that reference this Control Mode will remain unaffected.`}
            onCancel={() => setOpenDeleteDialouge(false)}
            onConfirm={() => {
              deleteSelectedControlMode();
              setOpenDeleteDialouge(false);
            }}
            confirm_button_text={`Delete`}
          />
        )}

        <ThermalDesigner />
      </div>
  );
};

export default Custom3DSat;
